import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MomentDateAdapter, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IModuleTranslationOptions, ModuleTranslateLoader } from '@larscom/ngx-translate-module-loader';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { environment } from '@environments/environment';

import { AppsModule } from '@apps/apps.module';
import { CoreModule } from '@core/core.module';
import { AppInitializationService } from '@core/init/app-initialization.service';
import { IdTokenOAuthInterceptor } from '@platform/auth/idtokenoauth.interceptor';
import { UnauthorizedInterceptor } from '@platform/auth/unauthorized.interceptor';
import { GlobalErrorHandler } from '@platform/shared/global-error-handler';
import { AngularCorrelationIdHttpInterceptor } from '@platform/shared/http/correlation-id.interceptor';
import { AngularDateHttpInterceptor } from '@platform/shared/http/date-parsing.interceptor';
import { AcceptLanguageHttpInterceptor } from '@platform/shared/http/language.interceptor';
import { filter, pairwise } from 'rxjs';
import { Scroll, Event, Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { provideQuillConfig } from 'ngx-quill/config';
// import { QuillModule } from 'ngx-quill';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppEffects } from './app.effects';
import { metaReducers, reducers } from './app.reducers';

//import mockServer from './mock-api/app.mock';

if (!environment.production) {
  //mockServer();
}

export class DynamicLocaleId extends String {
  constructor(protected service: TranslateService) {
    super('');
  }

  override toString() {
    return this.service.getCurrentLang();
  }
}

export function httpLoaderFactory(http: HttpClient) {
  const baseTranslateUrl = './assets/i18n';

  const options: IModuleTranslationOptions = {
    modules: [
      { baseTranslateUrl },
      { baseTranslateUrl: `${baseTranslateUrl}/apps`, moduleName: 'file-process' },
      { baseTranslateUrl: `${baseTranslateUrl}/apps`, moduleName: 'fiduciary' },
      { baseTranslateUrl: `${baseTranslateUrl}/apps`, moduleName: 'documents' },
      { baseTranslateUrl: `${baseTranslateUrl}/apps`, moduleName: 'projects' },
    ],
  };

  return new ModuleTranslateLoader(http, options);
}

export const initAppFn = () => {
  const appInitializationService = inject(AppInitializationService);
  return appInitializationService.initializeApp();
};

export const OBT_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatProgressBarModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictActionImmutability: false,
        strictStateImmutability: false,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      connectInZone: true,
    }),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
    }),
    EffectsModule.forRoot([AppEffects]),
    TranslateModule.forRoot({
      fallbackLang: 'de-CH',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiUrl],
        sendAccessToken: true,
      },
    }),
    LoggerModule.forRoot({
      serverLoggingUrl: '/api/v1/logs/client',
      level: NgxLoggerLevel.WARN,
      serverLogLevel: NgxLoggerLevel.WARN,
    }),
    AppRoutingModule,
    AppsModule,
    CoreModule,
  ],
  providers: [
    provideAppInitializer(initAppFn),
    { provide: HTTP_INTERCEPTORS, useClass: IdTokenOAuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AngularCorrelationIdHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AngularDateHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'de-CH' },
    // { provide: MAT_DATE_LOCALE, useClass: DynamicLocaleId, deps: [TranslateService] },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    provideMomentDateAdapter(OBT_DATE_FORMATS),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(withInterceptorsFromDi()),

    // required to run 'npm i parchment@3.0.0 && npm r parchment'
    provideQuillConfig({
      modules: {
        syntax: false,
        toolbar: [
          [
            'bold',
            'underline',
            'italic',
            'link',
            { list: 'bullet' },
            { color: [] },
            { background: [] },
            { font: [] },
            { size: ['small', false, 'large', 'huge'] },
            { align: [] },
          ],
          ['image'],
        ],
      },
    }),
  ],
})
export class AppModule {
  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller,
  ) {
    // Disable automatic scroll restoration to avoid race conditions
    this.viewportScroller.setHistoryScrollRestoration('manual');

    this.handleScrollOnNavigation();
  }

  /**
   * When route is changed, Angular interprets a simple query params change as "forward navigation" too.
   * Using the pairwise function allows us to have both the previous and current router events, which we can
   * use to effectively compare the two navigation events and see if they actually change route, or only
   * the route parameters (i.e. selections stored in query params).
   *
   * Related to: https://github.com/angular/angular/issues/26744
   * https://gist.github.com/iffa/9c820072135d25a6372d58075fe264dd
   */
  private handleScrollOnNavigation(): void {
    this.router.events
      .pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll),
        pairwise(),
      )
      .subscribe((e: Scroll[]) => {
        const previous = e[0] as any;
        const current = e[1] as any;
        if (current.position) {
          // Backward navigation
          this.viewportScroller.scrollToPosition(current.position);
        } else if (current.anchor) {
          // Anchor navigation
          this.viewportScroller.scrollToAnchor(current.anchor);
        } else if (!previous.routerEvent.urlAfterRedirects && !current.routerEvent.urlAfterRedirects) {
          // do nothing
        } else {
          // Check if routes match, or if it is only a query param change
          if (
            this.getBaseRoute(previous.routerEvent.urlAfterRedirects) !==
            this.getBaseRoute(current.routerEvent.urlAfterRedirects || current.routerEvent.url)
          ) {
            // Routes don't match, this is actual forward navigation
            // Default behavior: scroll to top
            this.viewportScroller.scrollToPosition([0, 0]);
          }
        }
      });
  }

  private getBaseRoute(url: string): string {
    // return url without query params
    if (!url) {
      return url;
    }
    return url.split('?')[0];
  }
}
